import { useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import type { AttemptQuestionDto } from "@mmm/contracts";
import { QUESTION_TYPE_LABEL_KEYS } from "../../../constants.js";
import { saveQuestionResponse, submitTestAttempt } from "../../../api/commands.js";
import { getTestAttempt } from "../../../api/queries.js";
import { useQuery } from "../../../api/useQuery.js";
import { flushAll } from "../../../api/dirtyTracker.js";
import { pushToast } from "../../../components/shell/ToastProvider.js";
import { useAutosave } from "../../../hooks/useAutosave.js";
import { useConfirm } from "../../../components/shell/ConfirmProvider.js";
import { LoadingRows, QueryStatus } from "../../../components/ui/LoadingPlaceholders.js";
import { useSubjectContext } from "../useSubjectContext.js";
import { Button, Card, ErrorState, ProgressBar, useT } from "@mmm/ui";
const LETTERS = "ABCDEFGHIJ";
// Bounds how long "Submit" waits for pending answer saves to flush before
// giving up and telling the student to retry â€” a dead API shouldn't hang the
// submit button forever, but drafts stay dirty (and keep retrying in the
// background) either way, so nothing already-typed is discarded by a timeout.
const SUBMIT_FLUSH_TIMEOUT_MS = 8000;

/**
 * One free-text question's answer box. Owns its own autosave session (via
 * the shared useAutosave hook â€” debounce + periodic retry + save-on-blur/
 * unmount + WP-08 dirty tracking) while the draft value itself stays lifted
 * to the parent screen so the answered-count/progress bar can see it.
 */
function FreeTextAnswer({
  attemptId,
  question,
  value,
  onChange,
}: {
  attemptId: string;
  question: AttemptQuestionDto;
  value: string;
  onChange: (value: string) => void;
}) {
  const t = useT();
  const { saveNow } = useAutosave(
    value,
    (next) => saveQuestionResponse({ attemptId, questionId: question.id, answerText: next }),
    { key: `attempt-answer:${attemptId}:${question.id}` }
  );

  return (
    <textarea
      className="mmm-textarea"
      style={{ minHeight: 110 }}
      placeholder={t("tests.writeYourAnswer")}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onBlur={saveNow}
    />
  );
}

function ChoiceAnswer({ attemptId, question, selectedIds, onChange }: { attemptId: string; question: AttemptQuestionDto; selectedIds: string[]; onChange: (optionId: string) => void }) {
  useAutosave(JSON.stringify(selectedIds), (value) => saveQuestionResponse({ attemptId, questionId: question.id, selectedAnswerOptionIds: JSON.parse(value) as string[] }), { key: `attempt-choice:${attemptId}:${question.id}`, debounceMs: 200 });
  const options = question.answerOptions.slice().sort((a, b) => a.sortOrder - b.sortOrder);
  return <>{options.map((option, index) => {
    const selected = selectedIds.includes(option.id);
    return <button key={option.id} type="button" role={question.type === "multiSelect" ? "checkbox" : "radio"}
      aria-checked={selected} className={`mmm-option-row mmm-option-row--interactive ${selected ? "is-selected" : ""}`.trim()}
      onClick={() => onChange(option.id)}>
      <span className="mmm-option-letter">{LETTERS[index] ?? index + 1}</span>
      <span className="mmm-option-text">{option.text}</span>
    </button>;
  })}</>;
}

export function TestAttemptScreen() {
  const { attemptId } = useParams();
  return <TestAttemptContents key={attemptId} />;
}

function TestAttemptContents() {
  const { subject } = useSubjectContext();
  const { testId = "", attemptId = "" } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const t = useT();
  const [choices, setChoices] = useState<Record<string, string[]>>({});
  const [submitting, setSubmitting] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const submitLockRef = useRef(false);

  const attemptQuery = useQuery(getTestAttempt, { attemptId });
  const { data: aggregate } = attemptQuery;

  if (attemptQuery.error && !attemptQuery.waiting) {
    return <ErrorState title={t("tests.loadTestAttemptFailed")} onRetry={attemptQuery.retry} />;
  }

  if (!aggregate) {
    return (
      <div className="mmm-test-screen">
        <QueryStatus queries={[attemptQuery]} />
        <div className="mmm-card" style={{ padding: 20 }}>
          <LoadingRows rows={5} />
        </div>
      </div>
    );
  }
  const { attempt, test, questions } = aggregate;

  const basePath = `/subjects/${subject.id}/tests/${testId}/attempts/${attemptId}`;

  if (attempt.status === "marked") return <Navigate to={`${basePath}/review`} replace />;
  if (attempt.status === "submitted") return <Navigate to={`${basePath}/mark`} replace />;

  const sorted = questions.slice().sort((a, b) => a.questionNumber - b.questionNumber);

  const isAnswered = (question: AttemptQuestionDto): boolean => {
    if (question.type === "freeText") {
      const draft = drafts[question.id];
      if (draft !== undefined) return draft.trim().length > 0;
      return Boolean(question.response?.answerText?.trim());
    }
    return (choices[question.id] ?? question.selectedAnswers.map((answer) => answer.answerOptionId)).length > 0;
  };

  const answeredCount = sorted.filter(isAnswered).length;

  const handleChoiceChange = (question: AttemptQuestionDto, optionId: string, multi: boolean) => {
    setChoices((previous) => {
      const existing = previous[question.id] ?? question.selectedAnswers.map((answer) => answer.answerOptionId);
      const next = multi ? existing.includes(optionId) ? existing.filter((id) => id !== optionId) : [...existing, optionId] : [optionId];
      return { ...previous, [question.id]: next };
    });
  };

  const handleSubmit = async () => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    try {
      const unanswered = sorted.length - answeredCount;
      const ok = await confirm({
        title: t("tests.submitAttempt"),
        message:
          unanswered > 0
            ? t("tests.submitUnansweredWarning", { count: unanswered })
            : t("tests.submitConfirm"),
        confirmLabel: t("common.submit"),
      });
      if (!ok) return;

      setSubmitting(true);
      // Flush every dirty answer draft before submitting — never lock in an
      // attempt with an answer that's still only sitting in local state.
      const flushed = await flushAll(SUBMIT_FLUSH_TIMEOUT_MS);
      if (!flushed) {
        pushToast({
          variant: "error",
          title: t("tests.saveAnswersFailedTitle"),
          message: t("tests.saveAnswersFailedMessage"),
        });
        return;
      }

      // Already toasted on failure — the Button's onClick invokes this without
      // awaiting it, so a rejection here must not escape uncaught, and must
      // not navigate away from an attempt that failed to submit.
      const result = await submitTestAttempt({ attemptId });
      navigate(result.attempt.status === "marked" ? `${basePath}/review` : `${basePath}/mark`);
    } catch {
      // The draft remains available after a failed submission.
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className="mmm-test-screen">
      <div className="mmm-attempt-header">
        <div>
          <h1 className="mmm-page-title" style={{ fontSize: 22 }}>
            {test.title}
          </h1>
          <p className="mmm-page-sublabel">{t("tests.attemptNumber", { number: attempt.attemptNumber })}</p>
        </div>
        <div className="mmm-attempt-progress">
          <span className="mmm-attempt-progress-label">
            {t("tests.answeredProgress", { answered: answeredCount, total: sorted.length })}
          </span>
          <ProgressBar value={answeredCount} max={sorted.length} variant="gradient" />
        </div>
      </div>

      <fieldset className="mmm-test-column mmm-form-fields" disabled={submitting}>
        {sorted.map((question) => {
          const selectedIds = choices[question.id] ?? question.selectedAnswers.map((s) => s.answerOptionId);

          return (
            <Card key={question.id} className="mmm-question-card">
              <div className="mmm-question-head">
                <span className={`mmm-question-num ${isAnswered(question) ? "is-answered" : ""}`.trim()}>
                  {question.questionNumber}
                </span>
                <span className="mmm-question-prompt">{question.prompt}</span>
                <span className="mmm-question-type">{t(QUESTION_TYPE_LABEL_KEYS[question.type])}</span>
              </div>

              <div className="mmm-question-body">
                {question.type === "freeText" ? (
                  <FreeTextAnswer
                    attemptId={attemptId}
                    question={question}
                    value={drafts[question.id] ?? question.response?.answerText ?? ""}
                    onChange={(value) => setDrafts((prev) => ({ ...prev, [question.id]: value }))}
                  />
                ) : (
                  <ChoiceAnswer attemptId={attemptId} question={question} selectedIds={selectedIds}
                    onChange={(optionId) => handleChoiceChange(question, optionId, question.type === "multiSelect")} />
                )}
              </div>
            </Card>
          );
        })}
      </fieldset>

      <div className="mmm-attempt-submitbar">
        <span style={{ color: "var(--mmm-muted)", fontSize: 13 }}>
          {answeredCount === sorted.length
            ? t("tests.allQuestionsAnswered")
            : t("tests.remaining", { count: sorted.length - answeredCount })}
        </span>
        <Button variant="gold" disabled={submitting} aria-busy={submitting} onClick={handleSubmit}>
          {t(submitting ? "tests.submittingTest" : "tests.submitTest")}
        </Button>
      </div>
    </div>
  );
}
